import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import toast from 'react-hot-toast';
import { 
  Key, 
  Plus, 
  Copy, 
  Trash2, 
  Check,
  Clock,
  CheckCircle,
  XCircle,
  Loader
} from 'lucide-react';

const AdminInviteCodes = () => {
  const { isSuperAdmin } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCodeNotes, setNewCodeNotes] = useState('');
  const [newCodeDays, setNewCodeDays] = useState(30);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await api.get('/admin/invite-codes');
      setCodes(response.data.codes);
    } catch (error) {
      console.error('Error fetching codes:', error);
      toast.error('Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    setCreating(true);
    try {
      const response = await api.post('/admin/invite-codes', { 
        notes: newCodeNotes || null,
        daysValid: newCodeDays
      });
      toast.success(`Code created: ${response.data.code.code}`);
      setShowCreateModal(false);
      setNewCodeNotes('');
      setNewCodeDays(30);
      fetchCodes();
    } catch (error) {
      console.error('Error creating code:', error);
      toast.error('Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleDeleteCode = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this invite code?')) return;
    
    try {
      await api.delete(`/admin/invite-codes/${id}`);
      toast.success('Code revoked');
      fetchCodes();
    } catch (error) {
      console.error('Error deleting code:', error);
      toast.error(error.response?.data?.error || 'Failed to revoke code');
    }
  };

  const getStatusBadge = (code) => {
    if (code.used) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          <CheckCircle size={12} /> Used
        </span>
      );
    }
    if (code.isExpired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle size={12} /> Expired
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
        <Clock size={12} /> Active
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const activeCount = codes.filter(c => !c.used && !c.isExpired).length;
  const usedCount = codes.filter(c => c.used).length;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-display">Invite Codes</h1>
          <p className="text-gray-600 mt-2">Each new account is automatically assigned to the administrator who created its code</p>
          <p className="text-sm text-gray-500 mt-1">
            {isSuperAdmin ? 'Showing invite codes created by all Managers and Super Admins.' : 'Showing only the invite codes you created.'}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} /> Generate Code
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <Clock className="text-green-500" size={24} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
            <p className="text-sm text-gray-500">Active Codes</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <CheckCircle className="text-blue-500" size={24} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{usedCount}</p>
            <p className="text-sm text-gray-500">Used</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
            <Key className="text-gray-500" size={24} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{codes.length}</p>
            <p className="text-sm text-gray-500">Total Generated</p>
          </div>
        </div>
      </div>

      {/* Codes List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {codes.length === 0 ? (
          <div className="text-center py-16">
            <Key className="mx-auto text-gray-300 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No invite codes yet</h3>
            <p className="text-gray-500 mb-4">Generate your first code to invite a customer</p>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              Generate First Code
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Notes</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Created By</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Used By</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Expires</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {codes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-md text-sm">
                          {code.code}
                        </code>
                        {!code.used && (
                          <button 
                            onClick={() => handleCopyCode(code.code)}
                            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                            title="Copy code"
                          >
                            {copiedCode === code.code ? (
                              <Check size={14} className="text-green-500" />
                            ) : (
                              <Copy size={14} className="text-gray-400" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {code.notes || <span className="text-gray-300">No notes</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {code.createdBy ? <div><p className="font-medium text-gray-800">{code.createdBy.name || 'Administrator'}</p><p className="text-xs text-gray-500">{code.createdBy.email}</p></div> : <span className="text-gray-300">Legacy code</span>}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(code)}</td>
                    <td className="px-6 py-4 text-sm">
                      {code.usedByCompany ? (
                        <div>
                          <p className="text-gray-800 font-medium">{code.usedByCompany}</p>
                          <p className="text-gray-500 text-xs">{code.usedByEmail}</p>
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(code.expiresAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!code.used && (
                        <button
                          onClick={() => handleDeleteCode(code.id)}
                          className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                          title="Revoke code"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate Invite Code</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  value={newCodeNotes}
                  onChange={(e) => setNewCodeNotes(e.target.value)}
                  className="input-field"
                  placeholder="e.g., Jane Smith at ABC Title Co"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Helps you track who the code is for
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valid for (days)
                </label>
                <input
                  type="number"
                  value={newCodeDays}
                  onChange={(e) => setNewCodeDays(parseInt(e.target.value) || 30)}
                  className="input-field"
                  min={1}
                  max={365}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-outline flex-1"
                disabled={creating}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCode}
                disabled={creating}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {creating ? <Loader className="animate-spin" size={18} /> : <Plus size={18} />}
                Generate Code
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInviteCodes;
